import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, ChevronLeft, Lock, LogOut, Save, UserRound } from 'lucide-react';
import axios from 'axios';
import { AvatarCropper } from '../components/AvatarCropper';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { UserAvatar } from '../components/UserAvatar';
import { useAuth } from '../context/AuthContext';

function errorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.error;
    if (typeof message === 'string' && message.trim()) return message;
  }
  return fallback;
}

export const AccountPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, changeLogin, changePassword, uploadAvatar, logout } = useAuth();

  const [loginValue, setLoginValue] = useState(user?.login || '');
  const [loginStatus, setLoginStatus] = useState<string | null>(null);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [savingLogin, setSavingLogin] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordStatus, setPasswordStatus] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [savingPassword, setSavingPassword] = useState(false);

  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [avatarSourceFile, setAvatarSourceFile] = useState<File | null>(null);
  const [croppedAvatarFile, setCroppedAvatarFile] = useState<File | null>(null);
  const [croppedAvatarPreviewUrl, setCroppedAvatarPreviewUrl] = useState<string | null>(null);
  const [avatarStatus, setAvatarStatus] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  useEffect(() => {
    if (user?.login) setLoginValue(user.login);
  }, [user?.login]);

  useEffect(() => {
    return () => {
      if (croppedAvatarPreviewUrl) URL.revokeObjectURL(croppedAvatarPreviewUrl);
    };
  }, [croppedAvatarPreviewUrl]);

  const clearCroppedAvatar = useCallback(() => {
    setCroppedAvatarFile(null);
    setCroppedAvatarPreviewUrl(null);
    setAvatarStatus(null);
  }, []);

  const resetAvatarDraft = useCallback(() => {
    setAvatarSourceFile(null);
    clearCroppedAvatar();
    if (avatarInputRef.current) {
      avatarInputRef.current.value = '';
    }
  }, [clearCroppedAvatar]);

  if (!user) return null;

  const handleLoginSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSavingLogin(true);
    setLoginError(null);
    setLoginStatus(null);
    try {
      await changeLogin(loginValue);
      setLoginStatus('Login updated.');
    } catch (error) {
      setLoginError(errorMessage(error, 'Unable to update login.'));
    } finally {
      setSavingLogin(false);
    }
  };

  const handlePasswordSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSavingPassword(true);
    setPasswordError(null);
    setPasswordStatus(null);
    try {
      await changePassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setPasswordStatus('Password updated.');
    } catch (error) {
      setPasswordError(errorMessage(error, 'Unable to update password.'));
    } finally {
      setSavingPassword(false);
    }
  };

  const handleAvatarSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!croppedAvatarFile) return;
    setUploadingAvatar(true);
    setAvatarError(null);
    setAvatarStatus(null);
    try {
      await uploadAvatar(croppedAvatarFile);
      resetAvatarDraft();
      setAvatarStatus('Avatar updated.');
    } catch (error) {
      setAvatarError(errorMessage(error, 'Unable to upload avatar.'));
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleAvatarFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setAvatarSourceFile(file);
    setAvatarError(null);
    setAvatarStatus(null);
    clearCroppedAvatar();
  };

  const handleAvatarConfirm = (file: File) => {
    setCroppedAvatarFile(file);
    setCroppedAvatarPreviewUrl(URL.createObjectURL(file));
    setAvatarError(null);
    setAvatarStatus('Crop ready.');
  };

  const handleAvatarCancel = () => {
    resetAvatarDraft();
    setAvatarError(null);
    setAvatarStatus(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-20 border-b-2 border-foreground bg-primary text-primary-foreground">
        <div className="mx-auto flex min-h-14 max-w-5xl flex-wrap items-center justify-between gap-2 px-3 py-2 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              <ChevronLeft className="h-4 w-4" />
              Home
            </Button>
            <div className="h-5 w-0.5 bg-primary-foreground" />
            <h1 className="truncate text-base font-black text-primary-foreground">Account</h1>
          </div>
          <Button variant="ghost" size="sm" onClick={logout}>
            <LogOut className="h-4 w-4" />
            Logout
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl space-y-5 px-3 py-5 sm:space-y-6 sm:px-6 sm:py-8">
        <section className="flex flex-col gap-4 border-b-2 border-foreground pb-6 sm:flex-row sm:items-center">
          <UserAvatar person={user} size="lg" />
          <div className="min-w-0">
            <h2 className="truncate text-2xl font-black text-foreground sm:text-3xl">{user.login}</h2>
            <p className="text-sm font-bold text-foreground/60">
              {user.avatarUrl ? 'Custom avatar active' : 'Using fallback avatar'}
            </p>
          </div>
        </section>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card>
            <CardContent className="space-y-4 p-4 sm:p-5">
              <div className="flex items-center gap-2">
                <UserRound className="h-4 w-4" />
                <h2 className="font-black">Login</h2>
              </div>
              <form onSubmit={(event) => void handleLoginSubmit(event)} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="account-login">Current login</Label>
                  <Input
                    id="account-login"
                    value={loginValue}
                    onChange={(event) => setLoginValue(event.target.value)}
                    autoComplete="username"
                    minLength={3}
                    maxLength={64}
                    required
                  />
                </div>
                {loginError && (
                  <p className="border-2 border-destructive bg-destructive px-3 py-2 text-sm font-bold text-destructive-foreground">
                    {loginError}
                  </p>
                )}
                {loginStatus && (
                  <p className="border-2 border-foreground bg-primary/20 px-3 py-2 text-sm font-bold">
                    {loginStatus}
                  </p>
                )}
                <Button type="submit" disabled={savingLogin || !loginValue.trim()}>
                  <Save className="h-4 w-4" />
                  {savingLogin ? 'Saving...' : 'Save login'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="space-y-4 p-4 sm:p-5">
              <div className="flex items-center gap-2">
                <Camera className="h-4 w-4" />
                <h2 className="font-black">Avatar</h2>
              </div>
              <form onSubmit={(event) => void handleAvatarSubmit(event)} className="space-y-4">
                <div className="flex items-center gap-3">
                  {croppedAvatarPreviewUrl ? (
                    <img
                      src={croppedAvatarPreviewUrl}
                      alt=""
                      className="h-10 w-10 rounded-full border-2 border-foreground object-cover shadow-neo-sm"
                    />
                  ) : (
                    <UserAvatar person={user} size="md" />
                  )}
                  <div className="min-w-0 text-sm font-bold text-foreground/60">
                    JPEG, PNG, or WebP up to 2 MB.
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="account-avatar">Image file</Label>
                  <Input
                    ref={avatarInputRef}
                    id="account-avatar"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleAvatarFileChange}
                  />
                </div>
                {avatarSourceFile && (
                  <p className="truncate text-xs font-mono font-bold text-foreground/60">
                    {avatarSourceFile.name}
                  </p>
                )}
                {avatarSourceFile && (
                  <AvatarCropper
                    file={avatarSourceFile}
                    onConfirm={handleAvatarConfirm}
                    onCancel={handleAvatarCancel}
                    onCropChange={clearCroppedAvatar}
                  />
                )}
                {avatarError && (
                  <p className="border-2 border-destructive bg-destructive px-3 py-2 text-sm font-bold text-destructive-foreground">
                    {avatarError}
                  </p>
                )}
                {avatarStatus && (
                  <p className="border-2 border-foreground bg-primary/20 px-3 py-2 text-sm font-bold">
                    {avatarStatus}
                  </p>
                )}
                <Button type="submit" disabled={uploadingAvatar || !croppedAvatarFile}>
                  <Camera className="h-4 w-4" />
                  {uploadingAvatar ? 'Uploading...' : 'Save avatar'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="space-y-4 p-4 sm:p-5">
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              <h2 className="font-black">Password</h2>
            </div>
            <form onSubmit={(event) => void handlePasswordSubmit(event)} className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_1fr_auto] md:items-end">
              <div className="space-y-1.5">
                <Label htmlFor="current-password">Current password</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  autoComplete="new-password"
                  minLength={6}
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={savingPassword || !currentPassword || newPassword.length < 6}
              >
                <Lock className="h-4 w-4" />
                {savingPassword ? 'Saving...' : 'Save password'}
              </Button>
            </form>
            {passwordError && (
              <p className="border-2 border-destructive bg-destructive px-3 py-2 text-sm font-bold text-destructive-foreground">
                {passwordError}
              </p>
            )}
            {passwordStatus && (
              <p className="border-2 border-foreground bg-primary/20 px-3 py-2 text-sm font-bold">
                {passwordStatus}
              </p>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};
